import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import {
  getAccountingFormData,
  getGeneralLedger,
} from "../../services/accountingService"



const formatMoney = (value, currency = "USD") =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))


const formatDate = (value) => {
  if (!value) return "-"
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat("fr-FR").format(date)
}


const extractErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (detail && typeof detail === "object") {
    return detail.message || detail.detail || fallback
  }

  return error?.message || fallback
}



export default function GeneralLedgerPage() {
  const [rows, setRows] = useState([])
  const [accounts, setAccounts] = useState([])
  const [accountId, setAccountId] = useState("all")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const loadData = useCallback(async () => {
    setLoading(true)
    setError("")

    try {
      const [ledgerResult, formResult] = await Promise.all([
        getGeneralLedger({
          account_id: accountId,
          date_from: dateFrom,
          date_to: dateTo,
        }),
        getAccountingFormData(),
      ])

      setRows(ledgerResult?.items || [])
      setAccounts(formResult?.accounts || [])
    } catch (err) {
      setError(extractErrorMessage(err, "Impossible de charger le grand livre."))
    } finally {
      setLoading(false)
    }
  }, [accountId, dateFrom, dateTo])

  useEffect(() => {
    loadData()
  }, [loadData])

  const columns = useMemo(
    () => [
      {
        field: "entry_date",
        headerName: "Date",
        minWidth: 120,
        valueFormatter: (value) => formatDate(value),
      },
      { field: "entry_code", headerName: "Écriture", minWidth: 150 },
      { field: "journal_code", headerName: "Journal", minWidth: 100 },
      { field: "account_code", headerName: "Compte", minWidth: 110 },
      { field: "account_name", headerName: "Libellé compte", minWidth: 220, flex: 1 },
      { field: "line_description", headerName: "Description", minWidth: 220, flex: 1 },
      {
        field: "debit",
        headerName: "Débit",
        minWidth: 130,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "credit",
        headerName: "Crédit",
        minWidth: 130,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "running_balance",
        headerName: "Solde",
        minWidth: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatMoney(value),
      },
    ],
    []
  )

  return (
    <MainLayout>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={900}>
              Grand livre
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Mouvements détaillés par compte avec solde cumulatif.
            </Typography>
          </Box>

          <Button variant="outlined" onClick={loadData} disabled={loading}>
            Actualiser
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr" },
              gap: 1.5,
            }}
          >
            <FormControl size="small">
              <InputLabel>Compte</InputLabel>
              <Select
                label="Compte"
                value={accountId}
                onChange={(event) => setAccountId(event.target.value)}
              >
                <MenuItem value="all">Tous les comptes</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {`${account.code} — ${account.name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Du"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />

            <TextField
              size="small"
              label="Au"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box sx={{ height: 650 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
              }}
              sx={{ border: 0 }}
            />
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  )
}
